import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';

function formatPercent(value: number): string {
  return `${Math.round(value * 100)}%`;
}

function formatCurrency(value: number | null, currency: string | null): string {
  if (value === null) return 'N/A';
  const normalizedCurrency = (currency || 'USD').toUpperCase();
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: normalizedCurrency,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatConversationLabel(conversationId: string): string {
  if (!conversationId) return 'Unknown conversation';
  if (conversationId.length <= 10) return conversationId;
  return `${conversationId.slice(0, 10)}…`;
}

export function AnalyticsPage() {
  const overviewQuery = useQuery({
    queryKey: ['analytics-overview'],
    queryFn: async () => {
      const [conversations, users, ragTelemetry, realtimeTelemetry, billingUsage] =
        await Promise.all([
          api.getConversations(),
          api.getUsers(),
          api.getRagTelemetry(30),
          api.getRealtimeTelemetry(30),
          api.getBillingUsage('month'),
        ]);

      const activeAgents = users.data.filter(
        (user) => user.messageCount > 0 || user.callCount > 0,
      ).length;
      const feedbackTotal = ragTelemetry.data.feedback.total;
      const ragAcceptanceRate =
        feedbackTotal > 0 ? ragTelemetry.data.feedback.accepted / feedbackTotal : 0;

      return {
        totalConversations: conversations.data.length,
        activeAgents,
        ragAcceptanceRate,
        realtimeFallbackRate: realtimeTelemetry.data.fallbackRate,
        monthlyCost: billingUsage.data.summary.totalCost,
        monthlyCurrency: billingUsage.data.summary.currency,
        providerSnapshots: billingUsage.data.providers,
        ragByConversation: ragTelemetry.data.byConversation.slice(0, 6),
        generatedAt: new Date(
          Math.max(
            new Date(ragTelemetry.data.generatedAt).getTime(),
            new Date(realtimeTelemetry.data.generatedAt).getTime(),
            new Date(billingUsage.data.generatedAt).getTime(),
          ),
        ).toISOString(),
      };
    },
    refetchInterval: 30_000,
  });
  const overviewCards = overviewQuery.data
    ? [
        {
          label: 'Total conversations',
          value: overviewQuery.data.totalConversations.toLocaleString(),
          icon: 'forum',
          helper: 'Across all tracked channels',
        },
        {
          label: 'Active agents',
          value: overviewQuery.data.activeAgents.toLocaleString(),
          icon: 'group',
          helper: 'Agents with current message/call activity',
        },
        {
          label: 'RAG acceptance',
          value: formatPercent(overviewQuery.data.ragAcceptanceRate),
          icon: 'check_circle',
          helper: 'Accepted AI suggestions in current window',
        },
        {
          label: 'Realtime fallback rate',
          value: formatPercent(overviewQuery.data.realtimeFallbackRate),
          icon: 'sync_problem',
          helper: 'Events requiring fallback transport',
        },
        {
          label: 'Monthly comms cost',
          value: formatCurrency(overviewQuery.data.monthlyCost, overviewQuery.data.monthlyCurrency),
          icon: 'payments',
          helper: 'Running provider costs this month',
        },
      ]
    : [];

  return (
    <div className="space-y-5">
      <header className="shell-surface p-5 sm:p-6">
        <h2 className="text-2xl font-semibold text-foreground">Analytics</h2>
        <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
          Live team performance from conversations, AI telemetry, and billing signals.
        </p>
      </header>

      {overviewQuery.isLoading ? (
        <div className="shell-surface p-6 text-sm text-muted-foreground">
          Loading analytics overview...
        </div>
      ) : overviewQuery.isError ? (
        <div className="rounded-2xl border border-red-400/40 bg-red-500/5 p-6 text-sm text-red-500">
          {(overviewQuery.error as Error).message}
        </div>
      ) : overviewQuery.data ? (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-5">
            {overviewCards.map((card) => (
              <div key={card.label} className="shell-surface relative p-5">
                <span className="text-sm text-muted-foreground">{card.label}</span>
                <span className="material-symbols-outlined absolute right-5 top-5 text-[18px] text-muted-foreground">
                  {card.icon}
                </span>
                <p className="mt-2 text-2xl font-semibold text-foreground">{card.value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{card.helper}</p>
              </div>
            ))}
          </div>

          <div className="grid gap-4 xl:grid-cols-[1.35fr_1fr]">
            <section className="shell-surface p-6">
              <h3 className="text-lg font-medium text-foreground">RAG feedback volume</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Top conversations by feedback count in the selected 30-day telemetry window.
              </p>

              {overviewQuery.data.ragByConversation.length === 0 ? (
                <p className="mt-4 text-sm text-muted-foreground">
                  No RAG feedback volume available yet.
                </p>
              ) : (
                <div className="mt-4 space-y-3">
                  {(() => {
                    const maxTotal = Math.max(
                      ...overviewQuery.data.ragByConversation.map((entry) => entry.total),
                      1,
                    );
                    return overviewQuery.data.ragByConversation.map((entry) => (
                      <div key={entry.conversationId} className="space-y-1.5">
                        <div className="flex items-center justify-between gap-3 text-xs">
                          <span className="text-foreground">
                            {formatConversationLabel(entry.conversationId)}
                          </span>
                          <span className="text-muted-foreground">{entry.total} votes</span>
                        </div>
                        <div className="h-2 rounded-full bg-muted">
                          <div
                            className="h-full rounded-full bg-brand-500 transition-all duration-300"
                            style={{
                              width: `${Math.max(8, Math.round((entry.total / maxTotal) * 100))}%`,
                            }}
                          />
                        </div>
                      </div>
                    ));
                  })()}
                </div>
              )}
            </section>

            <section className="shell-surface p-6">
              <h3 className="text-lg font-medium text-foreground">Provider billing signals</h3>
              <p className="mt-1 text-xs text-muted-foreground">
                Current-month provider rollups from Billing.
              </p>
              <div className="mt-4 space-y-3">
                {overviewQuery.data.providerSnapshots.map((provider) => (
                  <div
                    key={provider.provider}
                    className="rounded-xl border border-border bg-background/75 p-3"
                  >
                    <div className="flex items-center justify-between gap-2">
                      <p className="text-sm font-medium text-foreground">{provider.label}</p>
                      <span className="rounded-full border border-border bg-muted/70 px-2 py-0.5 text-[11px] text-muted-foreground">
                        {provider.status}
                      </span>
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground">
                      Records: {provider.records}
                    </p>
                    <p className="mt-1 text-xs text-muted-foreground">
                      Cost:{' '}
                      {formatCurrency(
                        provider.totalCost,
                        provider.currency ?? overviewQuery.data.monthlyCurrency,
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </section>
          </div>

          <p className="text-xs text-muted-foreground">
            Last refresh: {new Date(overviewQuery.data.generatedAt).toLocaleString()}
          </p>
        </>
      ) : null}
    </div>
  );
}
